// CB:BEGIN shared-header-property-manager
import React, { useState } from 'react';
import BottomSheet from './ui/BottomSheet';
import { useDevice } from '../hooks/useDevice';
import { SignedIn, SignedOut, UserButton, SignInButton, useUser, useClerk } from '@clerk/clerk-react';

interface SharedHeaderPropertyManagerProps {
  onNavigate: (screen: string) => void;
  activeScreen?: string;
  companyName?: string;
}

const BRAND_GREEN = '#123c3b';

const SharedHeaderPropertyManager: React.FC<SharedHeaderPropertyManagerProps> = ({
  onNavigate,
  activeScreen,
  companyName,
}) => {
  const { user } = useUser();
  const { openUserProfile } = useClerk();
  const [menuOpen, setMenuOpen] = useState(false);
  const { isMobileLayout } = useDevice();

  const navItems = [
    { label: 'Dashboard', id: 'nav-dashboard', screen: 'pm-dashboard' },
    { label: 'Properties', id: 'nav-properties', screen: 'pm-properties' },
    { label: 'Projects', id: 'nav-projects', screen: 'pm-projects' },
  ];

  const actionItems = [
    {
      label: 'Documents',
      onClick: () => onNavigate('pm-documents'),
    },
    {
      label: 'Help',
      onClick: () => {
        try { sessionStorage.setItem('cb.help.origin', 'pm-dashboard'); } catch {}
        onNavigate('pm-help');
      },
    },
    {
      label: 'Contact Us',
      href: 'mailto:support@corebids.com?subject=Property%20Manager%20Support',
    },
    {
      label: 'Settings',
      onClick: () => openUserProfile(),
    },
  ];

  const navBtnStyle = (screen: string): React.CSSProperties => ({
    background: activeScreen === screen ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.08)',
    color: 'white',
    border: activeScreen === screen ? '1px solid rgba(255,255,255,0.5)' : '1px solid rgba(255,255,255,0.2)',
    borderRadius: 8,
    padding: '7px 14px',
    fontSize: 13,
    fontWeight: activeScreen === screen ? 600 : 400,
    cursor: 'pointer',
  });

  const actionBtnStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.08)',
    color: 'rgba(255,255,255,0.85)',
    border: '1px solid rgba(255,255,255,0.18)',
    borderRadius: 8,
    padding: '7px 12px',
    fontSize: 12,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };

  const handleNavClick = (item: typeof navItems[0]) => {
    onNavigate(item.screen);
    if (item.anchor) {
      setTimeout(() => document.getElementById(item.anchor!)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  };

  return (
    <div
      style={{
        background: BRAND_GREEN,
        color: 'white',
        padding: isMobileLayout ? '12px 16px' : '0 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        height: isMobileLayout ? 'auto' : 60,
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation',
      }}
    >
      {/* Left: logo + role badge + nav + action buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobileLayout ? 10 : 12, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <span
            style={{ fontSize: isMobileLayout ? 18 : 20, fontWeight: 800, letterSpacing: '-0.03em', cursor: 'pointer', lineHeight: 1 }}
            onClick={() => onNavigate('pm-dashboard')}
          >
            CoreBids
          </span>
          <span style={{ fontSize: 10, fontWeight: 600, background: 'rgba(255,255,255,0.2)', color: 'white', padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap' }}>
            Property Manager
          </span>
        </div>

        {!isMobileLayout && (
          <SignedIn>
            {/* Nav buttons */}
            <nav style={{ display: 'flex', gap: 4 }}>
              {navItems.map(item => (
                <button key={item.id} style={navBtnStyle(item.screen)} onClick={() => handleNavClick(item)}>
                  {item.label}
                </button>
              ))}
            </nav>

            {/* Divider */}
            <span style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.2)', margin: '0 4px' }} />

            {/* Action buttons */}
            {actionItems.map(a => a.href ? (
              <a key={a.label} href={a.href} style={{ ...actionBtnStyle, textDecoration: 'none', display: 'inline-block' }}>{a.label}</a>
            ) : (
              <button key={a.label} style={actionBtnStyle} onClick={a.onClick}>{a.label}</button>
            ))}
          </SignedIn>
        )}
      </div>

      {/* Right: company name + avatar */}
      {!isMobileLayout && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
          <SignedIn>
            <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>
              {companyName || 'CoreBids PM'}
            </span>
            <UserButton afterSignOutUrl="/" />
          </SignedIn>
          <SignedOut>
            <SignInButton mode="modal">
              <button style={{ ...actionBtnStyle, fontSize: 13 }}>Sign In</button>
            </SignInButton>
          </SignedOut>
        </div>
      )}

      {/* Mobile: hamburger */}
      {isMobileLayout && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <SignedIn><UserButton afterSignOutUrl="/" /></SignedIn>
          <button
            aria-label="Menu"
            onClick={() => setMenuOpen(true)}
            style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: '1px solid rgba(255,255,255,0.2)', padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 16 }}
          >
            ☰
          </button>
        </div>
      )}

      {isMobileLayout && (
        <BottomSheet isOpen={menuOpen} onClose={() => setMenuOpen(false)} title="Menu">
          <div style={{ display: 'grid', gap: 10 }}>
            <SignedIn>
              {navItems.map(item => (
                <button key={item.id} className="btn btn-secondary"
                  style={{ color: 'black', fontWeight: activeScreen === item.screen ? 700 : 400 }}
                  onClick={() => { setMenuOpen(false); handleNavClick(item); }}
                >
                  {item.label}
                </button>
              ))}
              <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '4px 0' }} />
              {actionItems.map(a => a.href ? (
                <a key={a.label} href={a.href} className="btn btn-secondary"
                  style={{ color: 'black', textDecoration: 'none', textAlign: 'center' }}
                  onClick={() => setMenuOpen(false)}>
                  {a.label}
                </a>
              ) : (
                <button key={a.label} className="btn btn-secondary" style={{ color: 'black' }}
                  onClick={() => { setMenuOpen(false); a.onClick?.(); }}>
                  {a.label}
                </button>
              ))}

            </SignedIn>
            <SignedOut>
              <SignInButton mode="modal">
                <button className="btn btn-secondary" style={{ color: 'black' }}>Sign In</button>
              </SignInButton>
            </SignedOut>
          </div>
        </BottomSheet>
      )}
    </div>
  );
};

export default SharedHeaderPropertyManager;
// CB:END shared-header-property-manager
